import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { join_as_agent, join_as_vendor, service_provider } from "../../api/Role";
import { get_profile, get_settings } from "../../api/ControllApi";
import { useUser } from "../../context/LoginDetail";
import { toast } from "react-toastify";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CloseIcon from "@mui/icons-material/Close";

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { loadUser } = useUser();
  const [loadingRole, setLoadingRole] = useState(null);
  const [submittingRole, setSubmittingRole] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, item: null });
  const [loadingData, setLoadingData] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [settings, setSettings] = useState(null);

  const buttons = [
    {
      name: "Join As Agent",
      title: "Agent Request",
      confirmTitle: "Join as an Agent",
      description: "Submit a request to become a verified Agent on our platform. Once submitted, our team will review your application.",
      img: "/assets/icons/join_agent.png",
      role: "agent"
    },
    {
      name: "Join As Builder/Promoters",
      title: "Builder / Promoter Request",
      confirmTitle: "Join as Builder / Promoter",
      description: "Submit a request to list and promote properties as a Builder or Promoter. Our team will review and approve your application.",
      img: "/assets/icons/join_vendor.png",
      role: "vendor"
    },
    {
      name: "Join As Material/Service Provider",
      title: "Material / Service Provider Request",
      confirmTitle: "Join as Material / Service Provider",
      description: "Submit a request to offer construction materials or professional services on our platform. Our team will review your application.",
      img: "/assets/icons/service_provider.png",
      role: "service"
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [profileRes, settingsRes] = await Promise.all([
          get_profile(),
          get_settings()
        ]);
        setProfileData(profileRes?.data || null);
        setSettings(settingsRes?.data || null);
      } catch (err) {
        // toast.error("Failed to load dashboard data");
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenConfirm = (btn) => {
    setConfirmModal({ open: true, item: btn });
  };

  const handleCloseConfirm = () => {
    if (submittingRole) return;
    setConfirmModal({ open: false, item: null });
  };

  const handleSubmitRequest = async () => {
    const item = confirmModal.item;
    if (!item) return;
    const role = item.role;

    try {
      setSubmittingRole(true);
      setLoadingRole(role);
      let response;

      if (role === "agent") {
        response = await join_as_agent();
      } else if (role === "vendor") {
        response = await join_as_vendor();
      } else if (role === "service") {
        response = await service_provider();
      }

      if (response?.status === 200 || response?.status === "success") {
        toast.success(response?.message || "Request sent successfully!");

        // Update local profileData state immediately
        setProfileData((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (role === "agent") updated.agent_request = 1;
          if (role === "vendor") updated.vendor_request = 1;
          if (role === "service") updated.service_request = 1;
          if (response?.data) {
            return { ...updated, ...response.data };
          }
          return updated;
        });

        // Sync global user context
        if (loadUser) {
          loadUser();
        }

        // Re-fetch profile to refresh all backend relationship states
        try {
          const freshProfile = await get_profile();
          if (freshProfile?.data) {
            setProfileData(freshProfile.data);
          }
        } catch (e) {
          // ignore background sync error
        }

        handleCloseConfirm();
      } else {
        toast.error(response?.message || "Something went wrong!");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to submit request!";
      if (msg.toLowerCase().includes("already sent") || msg.toLowerCase().includes("already")) {
        setProfileData((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (role === "agent") updated.agent_request = 1;
          if (role === "vendor") updated.vendor_request = 1;
          if (role === "service") updated.service_request = 1;
          return updated;
        });
        toast.info(msg);
        handleCloseConfirm();
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmittingRole(false);
      setLoadingRole(null);
    }
  };

  const reachableAmount = parseFloat(profileData?.cumulative_amount) || 0;
  const targetAmount = parseFloat(settings?.cumulative_max_cost) || 0;
  const progress = targetAmount > 0 ? Math.min(100, (reachableAmount / targetAmount) * 100) : 0;

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "stretch",
          justifyContent: "space-between",
          gap: 3,
          mb: 4
        }}
      >
        {buttons.map((btn) => {
          // Approved check
          const isAgent =
            btn.role === "agent" &&
            (Number(profileData?.is_agent) === 1 ||
              Number(profileData?.role) === 2 ||
              profileData?.role_requests?.some(
                (r) => (r.requested_role === "Agent" || Number(r.role_id) === 2) && (r.status === "Approved" || r.status === "approved")
              ));

          const isVendor =
            btn.role === "vendor" &&
            (Number(profileData?.is_vendor) === 1 ||
              Number(profileData?.role) === 3 ||
              profileData?.role_requests?.some(
                (r) =>
                  (r.requested_role === "Builder / Promoter" || r.requested_role === "Vendor" || Number(r.role_id) === 3) &&
                  (r.status === "Approved" || r.status === "approved")
              ));

          const isService =
            btn.role === "service" &&
            (Number(profileData?.is_services) === 1 ||
              Number(profileData?.role) === 4 ||
              profileData?.role_requests?.some(
                (r) =>
                  (r.requested_role === "Material / Service Provider" || r.requested_role === "Service" || Number(r.role_id) === 4) &&
                  (r.status === "Approved" || r.status === "approved")
              ));

          // Pending check
          const agentPending =
            !isAgent &&
            btn.role === "agent" &&
            (Number(profileData?.agent_request) === 1 ||
              profileData?.role_requests?.some(
                (r) => (r.requested_role === "Agent" || Number(r.role_id) === 2) && (r.status === "Pending" || r.status === "pending")
              ));

          const vendorPending =
            !isVendor &&
            btn.role === "vendor" &&
            (Number(profileData?.vendor_request) === 1 ||
              profileData?.role_requests?.some(
                (r) =>
                  (r.requested_role === "Builder / Promoter" || r.requested_role === "Vendor" || Number(r.role_id) === 3) &&
                  (r.status === "Pending" || r.status === "pending")
              ));

          const servicePending =
            !isService &&
            btn.role === "service" &&
            (Number(profileData?.service_request) === 1 ||
              profileData?.role_requests?.some(
                (r) =>
                  (r.requested_role === "Material / Service Provider" || r.requested_role === "Service" || Number(r.role_id) === 4) &&
                  (r.status === "Pending" || r.status === "pending")
              ));

          const hasRole = isAgent || isVendor || isService;
          const isPending = agentPending || vendorPending || servicePending;
          const shouldDisable = hasRole || isPending;

          const getLabel = () => {
            if (isAgent) return "You Are Agent";
            if (isVendor) return "You Are Builder/Promoters";
            if (isService) return "You Are Material/Service Provider";

            if (agentPending) return "Agent Request Pending";
            if (vendorPending) return "Builder/Promoters Request Pending";
            if (servicePending) return "Material/Service Provider Request Pending";

            return btn.name;
          };

          return (
            <Button
              key={btn.name}
              onClick={() => !shouldDisable && handleOpenConfirm(btn)}
              disabled={loadingRole === btn.role || shouldDisable}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1.5,
                border: isPending
                  ? "1.5px solid #f59e0b"
                  : hasRole
                  ? "1.5px solid #10b981"
                  : "1px solid #d1d5db",
                borderRadius: 3,
                padding: "12px 18px",
                background: isPending
                  ? "#fffbeb"
                  : hasRole
                  ? "#f0fdf4"
                  : "white",
                color: isPending
                  ? "#b45309"
                  : hasRole
                  ? "#15803d"
                  : "#1f2937",
                ":hover": {
                  background: shouldDisable
                    ? isPending
                      ? "#fffbeb"
                      : "#f0fdf4"
                    : "#f9fafb",
                  borderColor: shouldDisable ? undefined : theme.palette.primary.main,
                  boxShadow: shouldDisable ? "none" : "0 4px 12px rgba(0,0,0,0.08)",
                },
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                width: { xs: "100%", sm: "auto" },
                flex: { sm: 1 },
                justifyContent: "flex-start",
                cursor: shouldDisable ? "default" : "pointer",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                transition: "all 0.2s ease-in-out",
                opacity: shouldDisable ? 1 : 1,
              }}
            >
              <img
                src={btn.img}
                alt={btn.name}
                style={{ width: 52, height: 52, objectFit: "contain" }}
              />

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "inherit" }}>
                  {loadingRole === btn.role ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    getLabel()
                  )}
                </Typography>
                {isPending && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <HourglassTopIcon sx={{ fontSize: 14, color: "#d97706" }} />
                    <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>
                      Request Pending Approval
                    </Typography>
                  </Box>
                )}
                {hasRole && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                    <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                      Role Approved
                    </Typography>
                  </Box>
                )}
              </Box>
            </Button>
          );
        })}
      </Box>

      {/* Confirmation Modal */}
      <Dialog
        open={Boolean(confirmModal.open)}
        onClose={handleCloseConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2937" }}>
            {confirmModal.item?.confirmTitle || "Confirm Request"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseConfirm}
            disabled={submittingRole}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
            {confirmModal.item?.img && (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "#f0fdf4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1.5,
                  border: "2px solid #bbf7d0"
                }}
              >
                <img
                  src={confirmModal.item.img}
                  alt={confirmModal.item.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
            )}

            <Typography sx={{ color: "#374151", fontSize: "0.95rem", lineHeight: 1.5 }}>
              {confirmModal.item?.description}
            </Typography>

            <Typography variant="caption" sx={{ color: "#6b7280", fontStyle: "italic" }}>
              Note: An administrator will review your application before this role is activated.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            onClick={handleCloseConfirm}
            disabled={submittingRole}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRequest}
            disabled={submittingRole}
            variant="contained"
            disableElevation
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              backgroundColor: "#166534",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#14532d" }
            }}
          >
            {submittingRole ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: "white" }} />
                <span>Submitting...</span>
              </Box>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referral Code Section */}
      {(Number(profileData?.is_vendor) === 1 || Number(profileData?.is_agent) === 1) && profileData?.code && (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            borderRadius: 4,
            backgroundColor: "#f0fdf4", // Soft green background
            border: "1px dashed #4ade80",
            mb: 4,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)"
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>
              Your Referral Code
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#166534", mt: 0.5 }}>
              {profileData.code}
            </Typography>
          </Box>
          <Button
            startIcon={<ContentCopyIcon />}
            variant="contained"
            disableElevation
            onClick={() => {
              navigator.clipboard.writeText(profileData.code);
              toast.success("Referral Code copied!");
            }}
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              backgroundColor: "#166534",
              "&:hover": { backgroundColor: "#14532d" },
              fontWeight: 700
            }}
          >
            Copy Code
          </Button>
        </Paper>
      )}

      {/* Graph and Stats Section (Matched to Image) */}
      <Box sx={{ width: "100%", mt: 5 }}>
        {/* Note : */}
        {settings?.cumulative_description && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              backgroundColor: "#fffdf0", // Soft cream background
              border: "1px solid #f9ebbe",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              mx: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#d4a017", mt: 0.2, fontSize: "1.2rem" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#856404",
                fontWeight: 500,
                lineHeight: 1.6,
                fontSize: "0.85rem",
                whiteSpace: "pre-line"
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                Note
                <br />
              </Box>

              {settings.cumulative_description.split("\r\n").map((line, i) => {
                const [title, ...rest] = line.split(":");
                const content = rest.join(":");

                return (
                  <span key={i}>
                    {title && <strong>{title}:</strong>} {content}
                    {"\n"}
                  </span>
                );
              })}
            </Typography>
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            width: "100%"
          }}
        >
          {/* Reachable Amount Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Total Gioo Nano Plots Purchased
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                fontSize: { xs: "1rem", sm: "1.2rem" },
                ml: 2
              }}
            >
              ₹ {reachableAmount.toLocaleString()}
            </Typography>
          </Box>

          {/* Target Goal Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Target Goal
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
                ml: 2
              }}
            >
              ₹ {targetAmount.toLocaleString()}
            </Typography>
          </Box>

          {/* Progress Bar Container */}
          <Box
            sx={{
              width: "100%",
              height: 16,
              backgroundColor: "#f0f2f5",
              borderRadius: 8,
              overflow: "hidden",
              position: "relative",
              mb: 4,
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)"
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: theme.palette.primary.main,
                borderRadius: 8,
                transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </Box>

          {/* Percentage Badge */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                px: 3,
                py: 0.75,
                borderRadius: "24px",
                fontSize: "0.95rem",
                fontWeight: 700,
                boxShadow: "0 2px 6px rgba(211, 47, 47, 0.3)"
              }}
            >
              {`${progress.toFixed(0)}%`} Done
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
